import { useEffect, useRef, useState } from "react";
import { usePayWithPoints } from "../../../../../services/hooks/loyalty/usePayWithPoints";
import { useCustomerAnalytics } from "../../../../../services/hooks/useCustomerAnalytics";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import { updateCurrentHomeScreenTab } from "../../../../../services/redux/slices/globalComponentsToggleSlice";
import {
  selectGuestTiersEnabled,
  selectUseLoyaltyService,
} from "../../../../../services/redux/selectors/loyaltyServiceSelectors";
import { CUSTOMER_ANALYTICS_EVENT, TabsEnum } from "../../../../../utils/enums";
import {
  getCurrentLoyaltyTier,
  getTierColor,
  getTierRank,
  sortLoyaltyTiers,
} from "../../../../../utils/loyalty/tiers";
import { RestaurantTypeEnum } from "../../../../../utils/types";
import { LoyaltyTierBadge } from "../../../../shared/LoyaltyTier/Badge/LoyaltyTierBadge";
import { LoyaltyTierMedal } from "../../../../shared/LoyaltyTier/Medal/LoyaltyTierMedal";
import styles from "./PreviousOrderTabComponent.module.scss";

export const PreviousOrderTab = (props: { currentTab: string }) => {
  const { t } = useTranslationI18();
  const addCustomerAnalytics = useCustomerAnalytics();
  const dispatch = useAppDispatch();

  const isFranchiseOpen = useAppSelector(
    (store) => store.franchiseStatus.currentIsOpen
  );

  const pointingSystem = useAppSelector(
    (store) => store.cartAndLoyaltyReducer.pointingSystem
  );

  const ecommerce = useAppSelector(
    (state) => state.config.restaurantInfo?.type
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";
  const isEcommerce: boolean = ecommerce === RestaurantTypeEnum.ECOMMERCE;
  const tiersEnabled = useAppSelector(selectGuestTiersEnabled);
  const customerInfo = useAppSelector(
    (state) => state.loyaltyCustomerInfo.data
  );
  const tierSystem = useAppSelector((state) => state.loyaltyTierSystem.data);
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);

  const hasLoyaltyProgram =
    !!pointingSystem || (useLoyaltyService && !!tierSystem);
  const tiers = sortLoyaltyTiers(tierSystem?.tiers);
  const currentTier = getCurrentLoyaltyTier(customerInfo, tierSystem);
  const tierColor =
    tiersEnabled && currentTier ? getTierColor(currentTier, tiers) : undefined;

  const { isEnabled: bannerOwnsTheMedal } = usePayWithPoints("browse");

  const tabs = [
    {
      title: TabsEnum.Menu,
      translation: t(isEcommerce ? "common:store" : "common:menu"),
    },
    {
      title: TabsEnum.Loyalty,
      translation: t(
        hasLoyaltyProgram ? "offers:loyalty" : "offers:offers-title"
      ),
      loyaltyStyle: hasLoyaltyProgram,
    },
    {
      title: TabsEnum.PreviousOrders,
      translation: t("common:my-orders"),
    },
  ];

  const handleClick = (currentView: string) => {
    if (currentView === TabsEnum.Loyalty) {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.REWARDS_PAGE_FROM_REWARDS_TAB
      );
    }
    if (currentView === TabsEnum.PreviousOrders) {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.VIEW_PREVIOUS_ORDERS_FROM_MY_ORDERS_TAB
      );
    }
    dispatch(updateCurrentHomeScreenTab(currentView));
  };

  const headerHeight =
    document.querySelector<HTMLElement>(`#_header_wrapper`)?.clientHeight;

  // The medal hangs off the tab bar's bottom edge and overlaps what follows -
  // which, at rest, is the loyalty section it belongs to. Both bars are sticky, so
  // scrolling brings the search row up underneath it and the medal (inside the
  // z-index 100 tab bar) covers the view toggles. Rather than guess a scroll
  // distance, this watches for the actual overlap and fades the medal out while it
  // lasts; it returns as soon as the member scrolls back to its section. Kept as
  // opacity so the element still has a box to measure.
  const medalRef = useRef<HTMLSpanElement>(null);
  const [overlapsSearchRow, setOverlapsSearchRow] = useState(false);

  useEffect(() => {
    if (!tiersEnabled) {
      setOverlapsSearchRow(false);

      return;
    }

    let pendingFrame: number | null = null;
    const measure = () => {
      pendingFrame = null;
      const medal = medalRef.current;
      const searchRow = document.getElementById("search_wrapper_bar");

      if (!medal || !searchRow) {
        setOverlapsSearchRow(false);

        return;
      }

      const medalBox = medal.getBoundingClientRect();
      const searchBox = searchRow.getBoundingClientRect();

      const overlaps =
        medalBox.bottom > searchBox.top && medalBox.top < searchBox.bottom;

      setOverlapsSearchRow((current) =>
        current === overlaps ? current : overlaps
      );
    };
    const scheduleMeasure = () => {
      if (pendingFrame === null) {
        pendingFrame = window.requestAnimationFrame(measure);
      }
    };

    scheduleMeasure();
    window.addEventListener("scroll", scheduleMeasure, { passive: true });
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      window.removeEventListener("scroll", scheduleMeasure);
      window.removeEventListener("resize", scheduleMeasure);
      if (pendingFrame !== null) {
        window.cancelAnimationFrame(pendingFrame);
      }
    };
  }, [tiersEnabled]);

  return (
    <div
      id="previous_order_bar"
      className={`${styles.container} ${!isFranchiseOpen && styles.closed} ${
        isMainDark ? "bg__zero" : "bg__02"
      }`}
      style={{ top: headerHeight && headerHeight }}
    >
      <div
        className={`${styles.container__tabs} ${styles.container__tabs__spaced}`}
      >
        {tabs.map((tab, index) => {
          return (
            <button
              id={tab.title}
              key={index}
              data-testid={tab.title + "_tab"}
              onClick={() => handleClick(tab.title)}
              className={`${styles.tab_btn} ${
                props.currentTab === tab.title
                  ? styles.tab_btn_active
                  : tab.loyaltyStyle && styles.tab_btn_loyalty
              } ${
                tab.title === TabsEnum.Loyalty && tiersEnabled
                  ? styles.tab_btn_tier
                  : ""
              }`}
              style={
                tab.title === TabsEnum.Loyalty && tierColor
                  ? ({ "--tier-color": tierColor } as React.CSSProperties)
                  : undefined
              }
            >
              {tab.title === TabsEnum.Loyalty &&
                tiersEnabled &&
                currentTier &&
                tierColor && (
                  <LoyaltyTierBadge
                    rank={getTierRank(currentTier, tiers)}
                    color={tierColor}
                    size="small"
                    showRank={false}
                  />
                )}
              {tab.translation}
            </button>
          );
        })}

        {/* Figma anchors the ribboned medal to the tab bar's bottom edge, where
            it overlaps whatever section follows. */}
        {tiersEnabled &&
          !bannerOwnsTheMedal &&
          currentTier &&
          tierColor &&
          props.currentTab !== TabsEnum.Loyalty && (
            <span
              ref={medalRef}
              className={`${styles.tab_medal} ${
                overlapsSearchRow ? styles.tab_medal_hidden : ""
              }`}
            >
              <LoyaltyTierMedal
                rank={getTierRank(currentTier, tiers)}
                color={tierColor}
              />
            </span>
          )}
      </div>
    </div>
  );
};
